import {StyleSheet} from "aphrodite";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {mediaQueries} from "@khan/shared-styles";
import {useMediaQuery} from "@khan/util-media-query";

type Props = Omit<React.ComponentProps<typeof View>, "style" | "children"> & {
    // The first column of the component
    firstColumn: React.ReactNode;
    // the second column of the component
    secondColumn: React.ReactNode;
    // styles applied to the container
    style?: StyleType;
    // styles applied to the first column
    firstColumnStyle?: StyleType;
    // styles applied to the second column
    secondColumnStyle?: StyleType;
    // the breakpoint for small screens
    smallScreenBreakpoint?: string;
    // whether to center the content in the middle of the container
    centered?: boolean;
};

/**
 * A layout with two columns. On small screens, the first column stacks above the second column.
 */
export const LayoutTwoColumns = ({
    firstColumn,
    secondColumn,
    style,
    firstColumnStyle,
    secondColumnStyle,
    smallScreenBreakpoint = mediaQueries.xsOrSmaller,
    centered = false,
    ...viewProps
}: Props) => {
    const isSmallScreen = useMediaQuery(smallScreenBreakpoint);

    const containerStyles = centered
        ? centeredStyles.container
        : styles.container;
    const smallScreenContainerStyles = centered
        ? centeredStyles.smallScreenContainer
        : styles.smallScreenContainer;
    const columnStyles = centered ? centeredStyles.column : styles.column;
    const smallScreenColumnStyles = centered
        ? centeredStyles.smallScreenColumn
        : null;
    const firstColumnStyles = centered ? centeredStyles.firstColumn : null;
    const secondColumnStyles = centered ? centeredStyles.secondColumn : null;

    return (
        <View
            style={[
                containerStyles,
                isSmallScreen && smallScreenContainerStyles,
                style,
            ]}
            {...viewProps}
        >
            <View
                style={[
                    columnStyles,
                    firstColumnStyles,
                    isSmallScreen && smallScreenColumnStyles,
                    firstColumnStyle,
                ]}
            >
                {firstColumn}
            </View>
            <View
                style={[
                    columnStyles,
                    secondColumnStyles,
                    isSmallScreen && smallScreenColumnStyles,
                    secondColumnStyle,
                ]}
            >
                {secondColumn}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
    },
    smallScreenContainer: {
        gridTemplateColumns: "1fr",
    },
    column: {
        alignItems: "center",
    },
});

const centeredStyles = StyleSheet.create({
    container: {
        display: "grid",
        gridTemplateColumns: "min-content min-content",
        margin: "auto",
    },
    column: {
        alignItems: "center",
    },
    smallScreenContainer: {
        gridTemplateColumns: "1fr",
    },
    smallScreenColumn: {
        justifySelf: "center",
    },
    firstColumn: {
        justifySelf: "flex-end",
    },
    secondColumn: {
        justifySelf: "flex-start",
    },
});
